"use client";

import { useEffect, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Knopf } from "../bausteine/knopf";
import { usePrelineOverlay } from "../bausteine/use-preline-overlay";
import { DEV_FEHLSCHLAG_SCHALTER, DEV_KI_STREIFEN_SATZ, satzFuerFehler } from "./dev-menue/dev-menue-logik";

/*
 * Betriebs- und Sicherheitshinweise der Shell (Impersonation, Umgebung,
 * Plattform-Mitteilungen, Wartung, Wartungs-Countdown). Daten und Aktionen
 * kommen unverändert aus dem Layout (dieselben Quellen wie im alten Layout);
 * hier nur die Darstellung in Preline.
 */

const SCHLIESSEN_ICON = (
  <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

/* Feste Klassenketten je Ton (Preline alerts-dismiss-button, Farbvarianten aus alerts-soft-color-variants). */
type HinweisTon = "info" | "warnung" | "fehler";
const RAHMEN: Record<HinweisTon, string> = {
  info: "bg-primary-100 border border-primary-200 text-sm text-primary-800 rounded-lg p-4 dark:bg-primary-500/20 dark:border-primary-900 dark:text-primary-400",
  warnung: "bg-yellow-100 border border-yellow-200 text-sm text-yellow-800 rounded-lg p-4 dark:bg-yellow-500/20 dark:border-yellow-900 dark:text-yellow-400",
  fehler: "bg-red-100 border border-red-200 text-sm text-red-800 rounded-lg p-4 dark:bg-red-500/20 dark:border-red-900 dark:text-red-400",
};
const KNOPF: Record<HinweisTon, string> = {
  info: "inline-flex bg-primary-100 rounded-lg p-1.5 text-primary-800 hover:bg-primary-200 focus:outline-hidden focus:bg-primary-200",
  warnung: "inline-flex bg-yellow-100 rounded-lg p-1.5 text-yellow-800 hover:bg-yellow-200 focus:outline-hidden focus:bg-yellow-200",
  fehler: "inline-flex bg-red-100 rounded-lg p-1.5 text-red-800 hover:bg-red-200 focus:outline-hidden focus:bg-red-200",
};
const TEXTKNOPF: Record<HinweisTon, string> = {
  info: "inline-flex items-center gap-x-1 rounded-lg py-1 px-2 text-sm font-medium text-primary-800 hover:bg-primary-200 focus:outline-hidden focus:bg-primary-200 disabled:opacity-50 disabled:pointer-events-none",
  warnung: "inline-flex items-center gap-x-1 rounded-lg py-1 px-2 text-sm font-medium text-yellow-800 hover:bg-yellow-200 focus:outline-hidden focus:bg-yellow-200 disabled:opacity-50 disabled:pointer-events-none",
  fehler: "inline-flex items-center gap-x-1 rounded-lg py-1 px-2 text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-hidden focus:bg-red-200 disabled:opacity-50 disabled:pointer-events-none",
};

/** Ein Hinweis-Streifen. Herkunft: siehe Herkunftskommentar im Markup. */
function Streifen({
  ton,
  rolle,
  titel,
  children,
  aktion,
}: {
  ton: HinweisTon;
  rolle: "alert" | "status";
  titel: React.ReactNode;
  children?: React.ReactNode;
  aktion?: React.ReactNode;
}) {
  return (
    <>
      {/* Preline: component/alerts/alerts-dismiss-button */}
      <div className={RAHMEN[ton]} role={rolle} tabIndex={-1}>
        <div className="flex">
          <div className="ms-2 min-w-0">
            <h3 className="text-sm font-medium">{titel}</h3>
            {children && <div className="mt-1 text-sm">{children}</div>}
          </div>
          {aktion && (
            <div className="ps-3 ms-auto">
              <div className="-mx-1.5 -my-1.5">{aktion}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Impersonation                                                       */
/* ------------------------------------------------------------------ */

export function ImpersonationHinweis({
  zielName,
  echterNutzer,
  zurueck,
}: {
  zielName: string;
  echterNutzer: string;
  zurueck: () => Promise<void>;
}) {
  const [pending, start] = useTransition();
  return (
    <Streifen
      ton="fehler"
      rolle="alert"
      titel={<>Angemeldet als {zielName}</>}
      aktion={
        <button
          type="button"
          className={TEXTKNOPF.fehler}
          disabled={pending}
          onClick={() => start(async () => { await zurueck(); })}
        >
          {pending ? "stoppe…" : "zurück"}
        </button>
      }
    >
      als Ziel · echter Nutzer: {echterNutzer}
    </Streifen>
  );
}

/* ------------------------------------------------------------------ */
/* KI-Vorschau „aus“ (Dev-Menü, nur dev/verify)                        */
/* ------------------------------------------------------------------ */

/**
 * Streifen am Seitenkopf, solange die KI-Vorschau läuft (W98/W100): macht sichtbar, dass die Seiten ohne KI gezeigt
 * werden, und bietet das Zurücksetzen. Der Server liefert ihn nur auf dev/verify und nur in der Entwicklersicht.
 */
export function KiVorschauHinweis({ zuruecksetzen }: { zuruecksetzen: (eingabe: { aus: boolean }) => Promise<unknown> }) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [fehler, setzeFehler] = useState<string | null>(null);
  return (
    <Streifen
      ton="info"
      rolle="status"
      titel={
        <>
          {DEV_KI_STREIFEN_SATZ} <span aria-hidden="true">·</span> nur diese Sitzung
        </>
      }
      aktion={
        <button
          type="button"
          className={TEXTKNOPF.info}
          disabled={pending}
          onClick={() =>
            start(async () => {
              setzeFehler(null);
              try {
                await zuruecksetzen({ aus: false });
                router.refresh();
              } catch (e) {
                setzeFehler(satzFuerFehler(e, DEV_FEHLSCHLAG_SCHALTER));
              }
            })
          }
        >
          Zurücksetzen
        </button>
      }
    >
      {fehler ?? "Die Seiten zeigen sich so, wie sie ohne KI aussehen."}
    </Streifen>
  );
}

/* ------------------------------------------------------------------ */
/* Umgebungs-/Betriebs-Dauerbalken (READ_ONLY_MODE, BANNER_MESSAGE, Stage) */
/* ------------------------------------------------------------------ */

/** Minimierbarer Dauerbalken, Zustand je `speicherSchluessel` im Browser (wie bisher EnvBanner). */
export function UmgebungsHinweis({ speicherSchluessel, text }: { speicherSchluessel: string; text: string }) {
  const [minimiert, setMinimiert] = useState(false);
  useEffect(() => {
    try {
      setMinimiert(localStorage.getItem(speicherSchluessel) === "1");
    } catch {
      // Speicher gesperrt: Balken bleibt sichtbar
    }
  }, [speicherSchluessel]);
  const setze = (v: boolean) => {
    setMinimiert(v);
    try {
      localStorage.setItem(speicherSchluessel, v ? "1" : "0");
    } catch {
      // Speicher gesperrt: nur für diese Ansicht
    }
  };

  if (minimiert) {
    return (
      <button type="button" className={TEXTKNOPF.warnung} onClick={() => setze(false)} aria-label="Hinweisbalken wieder einblenden">
        Hinweis einblenden
      </button>
    );
  }
  return (
    <Streifen
      ton="warnung"
      rolle="status"
      titel={text}
      aktion={
        <button type="button" className={TEXTKNOPF.warnung} onClick={() => setze(true)}>
          minimieren
        </button>
      }
    />
  );
}

/* ------------------------------------------------------------------ */
/* Plattform-Mitteilungen + Wartung                                    */
/* ------------------------------------------------------------------ */

export interface PlattformMitteilung {
  id: string;
  kind: "info" | "warning" | "critical" | "maintenance";
  title: string;
  body: string | null;
  link: string | null;
  linkLabel: string | null;
  dismissible: boolean;
  displayMode: "banner" | "overlay";
  activeUntilIso: string | null;
}

export interface WartungsDaten {
  enabled: boolean;
  mode: "banner_only" | "readonly" | "maintenance_page";
  displayMode: "banner" | "overlay";
  plannedStartIso: string | null;
  plannedEndIso: string | null;
  reason: string | null;
  contact: string | null;
}

const TON_JE_ART: Record<PlattformMitteilung["kind"], HinweisTon> = {
  info: "info",
  warning: "warnung",
  critical: "fehler",
  maintenance: "fehler",
};

function datumKurz(iso: string): string {
  // Explizite Zeitzone wie bisher (Server läuft in UTC).
  return new Date(iso).toLocaleString("de-DE", {
    timeZone: "Europe/Berlin",
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function datumLang(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", {
    timeZone: "Europe/Berlin", weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit",
  });
}

interface OverlayEintrag {
  id: string;
  kind: PlattformMitteilung["kind"];
  title: string;
  body: string | null;
  link: string | null;
  linkLabel: string | null;
  dismissible: boolean;
}

/**
 * Plattform-Mitteilungen und Wartungsmodus. Logik 1:1 wie bisher
 * (platform-banners.tsx + platform-overlay.tsx): Sortierung nach Schwere,
 * Wartung zuerst, Streifen vs. Overlay je displayMode, Ausblenden für mich
 * über dismissMessage + Neuladen; Wartung ist nie ausblendbar.
 */
export function PlattformHinweise({
  mitteilungen,
  wartung,
  ausblenden,
}: {
  mitteilungen: PlattformMitteilung[];
  wartung: WartungsDaten | null;
  ausblenden: (input: { messageId: string }) => Promise<unknown>;
}) {
  const router = useRouter();
  const [, start] = useTransition();
  const [verborgen, setVerborgen] = useState<Set<string>>(new Set());

  const handleAusblenden = (id: string) => {
    setVerborgen((prev) => new Set(prev).add(id));
    start(async () => {
      await ausblenden({ messageId: id });
      router.refresh();
    });
  };

  const reihenfolge = { critical: 0, maintenance: 1, warning: 2, info: 3 } as const;
  const sortiert = mitteilungen
    .filter((m) => !verborgen.has(m.id))
    .sort((a, b) => reihenfolge[a.kind] - reihenfolge[b.kind]);

  const streifen = sortiert.filter((m) => m.displayMode !== "overlay");
  const overlays: OverlayEintrag[] = sortiert
    .filter((m) => m.displayMode === "overlay")
    .map((m) => ({ id: m.id, kind: m.kind, title: m.title, body: m.body, link: m.link, linkLabel: m.linkLabel, dismissible: m.dismissible }));

  const wartungAlsOverlay: OverlayEintrag | null =
    wartung?.enabled && wartung.displayMode === "overlay"
      ? {
          id: "__maintenance__",
          kind: "maintenance",
          title: "Wartungsmodus aktiv",
          body: [
            wartung.reason,
            wartung.plannedStartIso ? `Beginn: ${datumLang(wartung.plannedStartIso)}` : null,
            wartung.plannedEndIso ? `Voraussichtliches Ende: ${datumLang(wartung.plannedEndIso)}` : null,
            wartung.contact ? `Kontakt: ${wartung.contact}` : null,
          ].filter(Boolean).join("\n\n"),
          link: null,
          linkLabel: null,
          dismissible: false,
        }
      : null;
  const alleOverlays = wartungAlsOverlay ? [wartungAlsOverlay, ...overlays] : overlays;
  const wartungAlsStreifen = wartung?.enabled && wartung.displayMode !== "overlay" ? wartung : null;

  if (streifen.length === 0 && alleOverlays.length === 0 && !wartungAlsStreifen) return null;

  return (
    <>
      {(streifen.length > 0 || wartungAlsStreifen) && (
        <div className="flex flex-col gap-y-2">
          {wartungAlsStreifen && (
            <Streifen ton="fehler" rolle="alert" titel="Wartungsmodus aktiv">
              {wartungAlsStreifen.reason && <>— {wartungAlsStreifen.reason} </>}
              {(wartungAlsStreifen.plannedStartIso || wartungAlsStreifen.plannedEndIso) && (
                <>
                  ·{wartungAlsStreifen.plannedStartIso ? ` ab ${datumKurz(wartungAlsStreifen.plannedStartIso)}` : ""}
                  {wartungAlsStreifen.plannedEndIso ? ` bis ${datumKurz(wartungAlsStreifen.plannedEndIso)}` : ""}{" "}
                </>
              )}
              {wartungAlsStreifen.contact && <>Kontakt: {wartungAlsStreifen.contact} </>}
              ({wartungAlsStreifen.mode === "banner_only" ? "nur Hinweis" : wartungAlsStreifen.mode === "readonly" ? "read-only (Phase 2)" : "Maintenance-Page (Phase 2)"})
            </Streifen>
          )}
          {streifen.map((m) => (
            <Streifen
              key={m.id}
              ton={TON_JE_ART[m.kind]}
              rolle={m.kind === "critical" ? "alert" : "status"}
              titel={m.title}
              aktion={
                m.dismissible ? (
                  <button type="button" className={KNOPF[TON_JE_ART[m.kind]]} onClick={() => handleAusblenden(m.id)} title="Für mich ausblenden">
                    <span className="sr-only">Für mich ausblenden</span>
                    {SCHLIESSEN_ICON}
                  </button>
                ) : undefined
              }
            >
              {m.body}
              {m.activeUntilIso && <> · bis {datumKurz(m.activeUntilIso)}</>}
              {m.link && (
                <>
                  {" "}
                  <Link
                    href={m.link}
                    target={m.link.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="underline underline-offset-4 font-medium"
                  >
                    {m.linkLabel ?? "öffnen"}
                  </Link>
                </>
              )}
            </Streifen>
          ))}
        </div>
      )}
      {/* key: je Mitteilung ein frisches Modal, damit nach dem Ausblenden die nächste aufgeht */}
      {alleOverlays[0] && <PlattformOverlay key={alleOverlays[0].id} eintraege={alleOverlays} ausblenden={handleAusblenden} />}
    </>
  );
}

/**
 * Großes Mitteilungs-Modal, eine Mitteilung nach der anderen (wie bisher).
 * Herkunft: siehe Herkunftskommentar im Markup (+ statischer Hintergrund, wenn nicht ausblendbar).
 */
function PlattformOverlay({ eintraege, ausblenden }: { eintraege: OverlayEintrag[]; ausblenden: (id: string) => void }) {
  const aktuell = eintraege[0];
  const ref = usePrelineOverlay<HTMLDivElement>(!!aktuell, () => {
    if (aktuell?.dismissible) ausblenden(aktuell.id);
  });
  if (!aktuell) return null;
  const titelId = `plattform-overlay-titel-${aktuell.id}`;

  return (
    <>
    {/* Preline: component/modal/modal-example */}
    <div
      ref={ref}
      id="hs-plattform-mitteilung"
      className={
        aktuell.dismissible
          ? "hs-overlay hs-overlay-open:opacity-100 hs-overlay-open:duration-500 hidden size-full fixed top-0 inset-s-0 z-80 opacity-0 overflow-x-hidden transition-all overflow-y-auto pointer-events-none"
          : "hs-overlay [--overlay-backdrop:static] hs-overlay-open:opacity-100 hs-overlay-open:duration-500 hidden size-full fixed top-0 inset-s-0 z-80 opacity-0 overflow-x-hidden transition-all overflow-y-auto pointer-events-none"
      }
      data-hs-overlay-keyboard={aktuell.dismissible ? undefined : "false"}
      role="dialog"
      tabIndex={-1}
      aria-labelledby={titelId}
    >
      <div className="sm:max-w-lg sm:w-full m-3 sm:mx-auto">
        <div className="flex flex-col bg-overlay border border-overlay-line shadow-2xs rounded-xl pointer-events-auto">
          <div className="flex justify-between items-center py-3 px-4 border-b border-overlay-header">
            <h3 id={titelId} className="font-semibold text-foreground">
              {aktuell.title}
            </h3>
            {aktuell.dismissible && (
              <Knopf variante="ghost" nurSymbol groesse="sm" label="Schließen" symbol={SCHLIESSEN_ICON} daten={{ "data-hs-overlay": "#hs-plattform-mitteilung" }} />
            )}
          </div>
          <div className="p-4 overflow-y-auto">
            {aktuell.body && <p className="mt-1 text-foreground whitespace-pre-wrap">{aktuell.body}</p>}
            {aktuell.link && (
              <p className="mt-3">
                <Knopf variante="primaer" href={aktuell.link} extern={aktuell.link.startsWith("http")}>
                  {aktuell.linkLabel ?? "Mehr erfahren"}
                </Knopf>
              </p>
            )}
          </div>
          {aktuell.dismissible && (
            <div className="flex justify-end items-center gap-x-2 py-3 px-4 border-t border-overlay-footer">
              <Knopf daten={{ "data-hs-overlay": "#hs-plattform-mitteilung" }}>Schließen</Knopf>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Wartungs-Countdown                                                  */
/* ------------------------------------------------------------------ */

/** Countdown bis zum nächsten Wartungsfenster (Logik wie maintenance-countdown.tsx). */
export function WartungsCountdown({ startIso, grund }: { startIso: string; grund: string | null }) {
  const [jetzt, setJetzt] = useState<number>(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setJetzt(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const restMs = new Date(startIso).getTime() - jetzt;
  if (restMs <= 0) return null;

  const gesamtMin = Math.round(restMs / 60_000);
  const tage = Math.floor(gesamtMin / (60 * 24));
  const stunden = Math.floor((gesamtMin % (60 * 24)) / 60);
  const minuten = gesamtMin % 60;

  let text: string;
  if (tage >= 2) text = `${tage} Tagen ${stunden} Std.`;
  else if (tage === 1) text = `1 Tag ${stunden} Std.`;
  else if (stunden >= 2) text = `${stunden} Std. ${minuten} Min.`;
  else if (stunden === 1) text = `1 Std. ${minuten} Min.`;
  else if (minuten > 0) text = `${minuten} Min.`;
  else text = "weniger als 1 Min.";

  const dringend = restMs < 60 * 60_000;
  return (
    <Streifen ton={dringend ? "fehler" : "warnung"} rolle="status" titel={<>Wartung beginnt in {text}</>}>
      {grund}
    </Streifen>
  );
}
